import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import type { DateInvite } from '@/api/types';
import { qk, useConfig, useDateInvites } from '@/api/queries';
import { useWsEvent } from '@/realtime';
import { isGuest, useAuth } from '@/stores/auth';
import { toast } from '@/stores/toasts';
import { notifyKeys } from './api';
import { lateLine } from '@/features/late/late';
import { currentEndpoint, permission, registerServiceWorker } from './push';
import { hadDate, hasScheduledDate, shouldAskPush, shouldOfferInstall, useNudges, type InstallPromptEvent } from './nudges';
import { InstallPrompt, PushSoftAsk } from './Nudges';

/**
 * v9 D, mounted once in App: registers the service worker, keeps the
 * `beforeinstallprompt` event for later, turns WS `date_invite.late` into
 * the lobby/Home state and a toast for the other person, notices the first
 * finished date, and shows at most one gentle nudge (push, then install).
 */
export function NotifyHost() {
  const authed = useAuth((s) => s.status === 'authed');
  const me = useAuth((s) => s.me);
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const config = useConfig();
  const pushKey = config.data?.push?.vapid_public_key ?? null;
  // Guest accounts (docs/SPEC_TRY.md) get none of this until they sign in.
  const member = authed && !!me?.onboarded && !isGuest(me);
  const invitesOn = member && config.data?.features.date_invites !== false;
  const invites = useDateInvites(invitesOn);
  const nudges = useNudges();
  const [subscribed, setSubscribed] = useState<boolean | null>(null);

  useEffect(() => {
    void registerServiceWorker();
    const onPrompt = (e: Event) => {
      e.preventDefault(); // keep it for after the first date
      useNudges.getState().setInstallEvent(e as InstallPromptEvent);
    };
    const onInstalled = () => useNudges.getState().markInstalled();
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!pushKey) return;
    let live = true;
    void currentEndpoint().then((e) => live && setSubscribed(!!e));
    return () => {
      live = false;
    };
  }, [pushKey]);

  const list = invites.data?.invites;
  useEffect(() => {
    if (hadDate(list) && !useNudges.getState().firstDateDone) useNudges.getState().markFirstDate();
  }, [list]);

  useWsEvent('date.state', (d) => {
    if (d?.date?.status === 'ended' && !useNudges.getState().firstDateDone) useNudges.getState().markFirstDate();
  });

  useWsEvent('date_invite.late', (d) => {
    if (!d?.invite_id || !d.late) return;
    qc.setQueryData(notifyKeys.late(d.invite_id), d.late);
    if (!me || d.by === me.id) return;
    const inv =
      qc.getQueryData<DateInvite>(qk.dateInvite(d.invite_id)) ?? qc.getQueryData<{ invites: DateInvite[] }>(qk.dateInvites)?.invites.find((i) => i.id === d.invite_id);
    const name = (inv?.role === 'host' ? inv.guest : inv?.host)?.display_name ?? 'Your date';
    const latest = d.late.notices.filter((n) => n.user_id === d.by).at(-1);
    if (!latest || pathname.startsWith('/call')) return;
    if (latest.kind === 'late') toast.people(lateLine(name, latest), 'We’ll keep the lobby open for you both.');
    else toast.people(`${name} can’t make it`, 'Let’s find a new time — open your date to pick one.');
  });

  if (!member) return null;
  const firstDateDone = nudges.firstDateDone;
  const scheduled = hasScheduledDate(list);
  const askPush =
    !!pushKey &&
    subscribed === false &&
    shouldAskPush({ available: true, permission: permission(), subscribed: false, scheduled, firstDateDone, path: pathname, asked: nudges.pushAsked });
  const offerInstall = !askPush && shouldOfferInstall({ canPrompt: !!nudges.installEvent, installed: nudges.installed, firstDateDone, path: pathname, asked: nudges.installAsked });
  const next = list?.find((i) => i.status === 'scheduled');
  const otherName = next ? (next.role === 'host' ? next.guest : next.host)?.display_name : null;
  return (
    <AnimatePresence>
      {askPush && pushKey ? <PushSoftAsk key="push" vapidKey={pushKey} otherName={otherName} /> : offerInstall ? <InstallPrompt key="install" /> : null}
    </AnimatePresence>
  );
}
